import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'
import DashboardContainer from 'components/smart/dashboard-container/DashboardContainer'
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  OutlinedInput,
  Grid,
  Alert,
  Collapse,
  Skeleton,
  MenuItem,
  Select,
  FormControl,
  TablePagination,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AdxHeaderButton from 'components/reusable/AdxHeaderButton/AdxHeaderButton'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import TaskProjectCard from 'module/task-management/components/ProjectTrackerCard'
import ProjectManager from './Manager'
import CreateProjectModal from './CreateProjectModal'
import EditProjectModal from './EditProjectModal'
import TrackerCalendar from './TrackerCalendar'
import MarkProjectCompleteModal from './MarkProjectCompleteModal'
import TrackerGanttChart from 'module/task-management/gantt-chart'
import {
  useGetProjectTrackerProjects,
  useGetProjectTrackerTasks,
  useGetMyProjects,
  useMyAccount,
  useCompleteProjectTrackerProject,
  PROJECT_TRACKER_PROJECTS_QK,
  PROJECT_TRACKER_TASKS_QK,
} from 'services/api/task-management/trackerHooks'
import { useLocation } from 'react-router-dom'
import {
  filterVisibleProjectTrackerStaff,
  isHiddenProjectTrackerStaff,
} from '../helpers/hiddenStaff'

const ENABLE_GANTT_CHART = true

const pillSx = {
  height: 36,
  borderRadius: 999,
  // border: '1px solid rgba(0,0,0,0.45)',
  backgroundColor: '#fff',
  px: 0,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.1)',
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.1)',
    borderWidth: '1px',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(0,0,0,0.1)',
    borderWidth: '1px',
  },
}

function PillSelect({
  value,
  onChange,
  icon,
  placeholder,
  items,
  minWidth = 220,
  allowEmpty = false,
  emptyValue = '',
  forceGrey = false,
  bgColor = '#F7F9FA',
}) {
  const isEmpty = value === emptyValue || value === null || value === undefined
  const showGrey = forceGrey || (allowEmpty && isEmpty)

  const getLabel = (selected) => {
    if (allowEmpty && (selected === emptyValue || selected == null)) return placeholder
    const found = items.find((i) => i.value === selected)
    return found ? found.label : placeholder
  }

  return (
    <FormControl size="small" sx={{ minWidth }}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty={allowEmpty}
        input={
          <OutlinedInput
            sx={{
              ...pillSx,
              backgroundColor: bgColor,
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                padding: '0 8px',
                height: 44,
                boxSizing: 'border-box',
              },
              '& .MuiSelect-icon': {
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
              },
            }}
          />
        }
        renderValue={(selected) => {
          const label = getLabel(selected)

          return (
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '24px 1fr 24px',
                alignItems: 'center',
                columnGap: '10px',
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: '2px',
                  '& svg': { display: 'block' },
                }}
              >
                {icon}
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  color: showGrey ? '#8A8A8A' : 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {label}
              </Box>

              <Box sx={{ width: 24 }} />
            </Box>
          )
        }}
      >
        {allowEmpty && <MenuItem value={emptyValue}>{placeholder}</MenuItem>}
        {items.map((it) => (
          <MenuItem key={it.value} value={it.value}>
            {it.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

function EmptyProjectStateCard() {
  return (
    <Box
      sx={{
        border: '1px solid rgba(0,0,0,0.14)',
        borderRadius: 3,
        p: 3,
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path
            d="M25.6663 18.666V10.666C25.6663 6.89535 25.6663 5.00868 24.4943 3.83802C23.3237 2.66602 21.437 2.66602 17.6663 2.66602H12.333C8.56234 2.66602 6.67567 2.66602 5.50501 3.83802C4.33301 5.00868 4.33301 6.89535 4.33301 10.666V21.3327C4.33301 25.1033 4.33301 26.99 5.50501 28.1607C6.67567 29.3327 8.56234 29.3327 12.333 29.3327H17.6663"
            stroke="#8A8A8A"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.66602 19.9993H14.9993M9.66602 14.666H20.3327M28.3327 22.666L24.9993 25.9993M24.9993 25.9993L21.666 29.3327M24.9993 25.9993L28.3327 29.3327M24.9993 25.9993L21.666 22.666M19.666 2.66602H10.3327C10.3327 4.55135 10.3327 5.49402 10.9193 6.07935C11.5047 6.66602 12.4473 6.66602 14.3327 6.66602H15.666C17.5513 6.66602 18.494 6.66602 19.0793 6.07935C19.666 5.49402 19.666 4.55135 19.666 2.66602Z"
            stroke="#8A8A8A"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>

      <Typography variant="subtitle1" sx={{ color: '#3A3A3A', fontWeight: 500 }}>
        No project yet.
      </Typography>

      <Typography variant="body1" sx={{ color: '#3A3A3A', fontWeight: 400 }}>
        Please create a project to get started.
      </Typography>
    </Box>
  )
}

function ProjectTrackerLoadingState({ projectCardsPerRow = 3, selectedManagerId = null }) {
  const managerColumns = selectedManagerId ? 1 : 3

  return (
    <Grid container spacing={3} sx={{ pb: 6 }}>
      {Array.from({ length: managerColumns }).map((_, index) => (
        <Grid item xs={12} md={selectedManagerId ? 12 : 4} key={index}>
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Skeleton variant="circular" width={21} height={21} />
              <Skeleton variant="text" width={140} height={28} />
            </Stack>

            <Grid container spacing={3}>
              {Array.from({ length: selectedManagerId ? projectCardsPerRow : 1 }).map((__, cardIndex) => (
                <Grid item xs={12} md={selectedManagerId ? 12 / projectCardsPerRow : 12} key={cardIndex}>
                  <Box
                    sx={{
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 3,
                      p: 2.5,
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    <Skeleton variant="text" width="58%" height={30} />
                    <Skeleton variant="text" width="82%" height={22} />
                    <Skeleton variant="text" width="64%" height={22} sx={{ mb: 2 }} />
                    <Skeleton variant="rounded" width="100%" height={72} sx={{ borderRadius: 2 }} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Grid>
      ))}
    </Grid>
  )
}

const ProjectTracker = () => {
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl'))
  const managersPerPage = isMobile ? 1 : 3
  const managerOverviewProjectsLimit = 3
  const projectCardsPerRow = isMobile ? 1 : isExtraLarge ? 3 : 2
  const selectedManagerProjectsPerPage = isMobile ? 3 : isExtraLarge ? 6 : 4

  const isLarge = useMediaQuery(theme.breakpoints.up('lg'))

  const [menuShown, setMenuShown] = useState(() => {
    const saved = localStorage.getItem('menuShown')
    return saved !== null ? JSON.parse(saved) : true
  })

  useEffect(() => {
    // same-tab changes won't fire "storage", so poll is the most reliable w/o touching SideMenu
    const t = window.setInterval(() => {
      const saved = localStorage.getItem('menuShown')
      const next = saved !== null ? JSON.parse(saved) : true
      setMenuShown((prev) => (prev === next ? prev : next))
    }, 300)

    return () => window.clearInterval(t)
  }, [])

  const sidebarWidth = isLarge ? (menuShown ? 320 : 100) : 0

  const [activeTab, setActiveTab] = useState(0)
  const calendarTabIndex = ENABLE_GANTT_CHART ? 2 : 1
  const [openCreateProjectModal, setOpenCreateProjectModal] = useState(false)
  const [projectModalMode, setProjectModalMode] = useState('create')
  const [duplicateInitialValues, setDuplicateInitialValues] = useState(null)
  const [managerPage, setManagerPage] = useState(1)
  const [selectedManagerProjectPage, setSelectedManagerProjectPage] = useState(1)
  const [openEditProjectModal, setOpenEditProjectModal] = useState(false)
  const [editingProjectId, setEditingProjectId] = useState(null)
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('ongoing')
  const [statusTouched, setStatusTouched] = useState(false)
  const [manager, setManager] = useState('')
  const [sort, setSort] = useState('due_soonest')
  const [sortTouched, setSortTouched] = useState(false)

  const statusItems = [
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'completed', label: 'Completed' },
  ]
  const sortItems = [
    { value: 'due_soonest', label: 'Due Soonest' },
    { value: 'due_latest', label: 'Due Latest' },
  ]

  const { data: myAccRes, isLoading: myAccLoading } = useMyAccount()
  const myStaffId = myAccRes?.data?.staff?.id
  const myStaffObj = myAccRes?.data?.staff ?? null
  const myStaffPermissions = myAccRes?.data?.staff?.permissions ?? []
  const canViewAllGanttProjects = Boolean(
    myAccRes?.data?.super_admin || myStaffPermissions.length
  )

  const { data: projectsResponse, isLoading: projectsLoading } = useGetProjectTrackerProjects()
  const { data: myProjectsResponse, isLoading: myProjectsLoading } = useGetMyProjects(
    {},
    {
      enabled: !canViewAllGanttProjects,
    }
  )
  const { data: tasksResponse, isLoading: tasksLoading } = useGetProjectTrackerTasks()
  const { mutateAsync: completeProject, isLoading: completingProject } =
    useCompleteProjectTrackerProject()

  const isMemberOfProject = useCallback((proj, staffId) =>
    !!staffId && (proj?.members ?? []).some((m) => m?.staff_id === staffId)
  , [])

  const isManagerOfProject = useCallback((proj, staffId) =>
    !!staffId &&
    (proj?.members ?? []).some(
      (m) => m?.staff_id === staffId && String(m?.role ?? '').toLowerCase() === 'manager'
    )
  , [])

  const isProjectMasterOfProject = useCallback((proj, staffId) =>
    !!staffId && proj?.created_by != null && String(proj.created_by) === String(staffId)
  , [])

  const canEditProject = useCallback((proj, staffId) =>
    isManagerOfProject(proj, staffId) || isProjectMasterOfProject(proj, staffId)
  , [isManagerOfProject, isProjectMasterOfProject])

  const getManagersOfProject = (proj) =>
    (proj?.members ?? [])
      .filter((m) => m?.role === 'manager' && (m?.staff || m?.staff_id))
      .map((m) => ({
        id: m?.staff?.id ?? m?.staff_id,
        staff: m?.staff ?? null,
        name: m?.staff?.name ?? '',
      }))
      .filter((x) => x?.id)

  const buildDuplicateProjectInitialValues = (project) => {
    const managerStaff = (project?.members ?? [])
      .filter((member) => String(member?.role ?? '').toLowerCase() === 'manager')
      .map((member) => member?.staff ?? null)
      .filter(Boolean)

    const dedupedManagers = Array.from(
      new Map(managerStaff.map((staff) => [String(staff.id), staff])).values()
    )

    return {
      projectName: project?.name ?? '',
      objective: project?.objective ?? '',
      background: project?.background ?? '',
      link: '',
      startDate: '',
      endDate: '',
      projectManagers: dedupedManagers,
      selected: [],
    }
  }

  const handleCreateProject = () => {
    setProjectModalMode('create')
    setDuplicateInitialValues(null)
    setOpenCreateProjectModal(true)
  }
  const handleMainTabChange = (event, newValue) => setActiveTab(newValue)

  const handleDuplicateProject = (proj) => {
    if (!proj?.id) return
    if (!isProjectMasterOfProject(proj, myStaffId)) return

    setProjectModalMode('duplicate')
    setDuplicateInitialValues(buildDuplicateProjectInitialValues(proj))
    setOpenCreateProjectModal(true)
  }

  const handleOpenEdit = (proj) => {
    if (!proj?.id) return
    if (!canEditProject(proj, myStaffId)) return
    setEditingProjectId(proj.id)
    setOpenEditProjectModal(true)
  }

  const handleCloseEdit = () => {
    setOpenEditProjectModal(false)
    setEditingProjectId(null)
  }

  const normalizeProjectStatus = (raw) => {
    const s = String(raw || '').toLowerCase().trim()
    if (s === 'on_going' || s === 'ongoing' || s === 'on-going') return 'ongoing'
    if (s === 'completed' || s === 'complete') return 'completed'
    return s
  }

  const toDeadlineTs = (p) => {
    const d = p?.deadline
    const ts = d ? Date.parse(d) : NaN
    return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY
  }

  const sortProjectsByDeadline = (projects, sortMode) => {
    const dir = sortMode === 'due_latest' ? -1 : 1
    return [...(projects ?? [])].sort((a, b) => (toDeadlineTs(a) - toDeadlineTs(b)) * dir)
  }

  const allProjects = useMemo(() => projectsResponse?.data?.projects ?? [], [projectsResponse])
  const ganttProjects = useMemo(() => {
    if (canViewAllGanttProjects) return allProjects
    return myProjectsResponse?.data?.projects ?? []
  }, [allProjects, canViewAllGanttProjects, myProjectsResponse])
  const ganttTasks = useMemo(() => {
    const visibleProjectIds = new Set((ganttProjects ?? []).map((project) => String(project?.id)))
    return (tasksResponse?.data?.tasks ?? []).filter((task) =>
      visibleProjectIds.has(String(task?.project_id))
    )
  }, [ganttProjects, tasksResponse])

  const allProjectCards = useMemo(() => {
    const projects = allProjects ?? []
    const allTasks = tasksResponse?.data?.tasks ?? []
    if (!projects.length) return []

    const projectIdSet = new Set(projects.map((p) => p.id))
    const relevantTasks = allTasks.filter((t) => projectIdSet.has(t?.project_id))

    const tasksByProjectId = new Map()
    for (const t of relevantTasks) {
      const pid = t?.project_id
      if (!pid) continue
      if (!tasksByProjectId.has(pid)) tasksByProjectId.set(pid, [])
      tasksByProjectId.get(pid).push(t)
    }

    return projects.map((p) => ({
      ...p,
      tasks: tasksByProjectId.get(p.id) ?? [],
    }))
  }, [allProjects, tasksResponse])

  const qNorm = useMemo(() => (q || '').trim().toLowerCase(), [q])
  const isSearching = qNorm.length > 0
  const showEmptyManagers = !isSearching && status === 'ongoing'

  const filteredProjectCards = useMemo(() => {
    let list = allProjectCards
    if (qNorm) list = list.filter((p) => (p?.name || '').toLowerCase().includes(qNorm))
    if (status) list = list.filter((p) => normalizeProjectStatus(p?.status) === status)
    return list
  }, [allProjectCards, qNorm, status])

  const myMemberProjects = useMemo(() => {
    if (!myStaffId) return []
    return filteredProjectCards.filter((p) => isMemberOfProject(p, myStaffId) || isProjectMasterOfProject(p, myStaffId))
  }, [filteredProjectCards, isMemberOfProject, isProjectMasterOfProject, myStaffId])

  const myManagedProjects = useMemo(() => {
    if (!myStaffId) return []
    return filteredProjectCards.filter((p) => canEditProject(p, myStaffId))
  }, [canEditProject, filteredProjectCards, myStaffId])

  const myMemberButNotManagedProjects = useMemo(() => {
    if (!myStaffId) return []
    return myMemberProjects.filter((p) => !canEditProject(p, myStaffId))
  }, [canEditProject, myMemberProjects, myStaffId])

  const otherManagers = useMemo(() => {
    const list = []
    for (const proj of myMemberButNotManagedProjects) {
      const mgrs = getManagersOfProject(proj)
      for (const m of mgrs) {
        if (m.id === myStaffId) continue
        const managerStaff = m.staff ? { ...m.staff, id: m.id } : { id: m.id, name: m.name }
        if (isHiddenProjectTrackerStaff(managerStaff)) continue
        list.push(managerStaff)
      }
    }

    const unique = filterVisibleProjectTrackerStaff(
      Array.from(new Map(list.map((s) => [s?.id, s])).values()).filter((s) => s?.id)
    )
    unique.sort((a, b) =>
      (a?.name ?? '').localeCompare(b?.name ?? '', undefined, { sensitivity: 'base' })
    )
    return unique
  }, [myMemberButNotManagedProjects, myStaffId])

  const managersForDisplay = useMemo(() => {
    const hasMe = !!(myStaffObj && myStaffId)
    const me = hasMe ? { ...myStaffObj, name: 'You', id: myStaffId } : null
    const includeMe = !!me && (showEmptyManagers || (myManagedProjects?.length ?? 0) > 0)
    return includeMe ? [me, ...otherManagers] : otherManagers
  }, [myStaffObj, myStaffId, otherManagers, myManagedProjects, showEmptyManagers])

  const managerItems = useMemo(() => {
    return (managersForDisplay ?? [])
      .filter((s) => s?.id && s?.name)
      .map((s) => ({ value: String(s.id), label: s.name }))
  }, [managersForDisplay])

  const projectsByManagerId = useMemo(() => {
    const map = new Map()

    if (myStaffId && (!isSearching || (myManagedProjects?.length ?? 0) > 0)) {
      map.set(myStaffId, myManagedProjects)
    }

    for (const m of otherManagers) map.set(m.id, [])

    for (const proj of myMemberButNotManagedProjects) {
      const mgrs = getManagersOfProject(proj)
      for (const mgr of mgrs) {
        if (!map.has(mgr.id)) continue
        map.get(mgr.id).push(proj)
      }
    }

    return map
  }, [myStaffId, myManagedProjects, otherManagers, myMemberButNotManagedProjects, isSearching])

  const selectedManagerId = useMemo(() => {
    if (!manager) return null
    const n = Number(manager)
    return Number.isFinite(n) ? n : null
  }, [manager])

  const managersForUi = useMemo(() => {
    if (!selectedManagerId) return managersForDisplay
    return managersForDisplay.filter((m) => m?.id === selectedManagerId)
  }, [managersForDisplay, selectedManagerId])

  const totalManagerPages = useMemo(() => {
    if (selectedManagerId) return 1
    return Math.max(1, Math.ceil(managersForUi.length / managersPerPage))
  }, [selectedManagerId, managersForUi.length, managersPerPage])

  const pagedManagers = useMemo(() => {
    if (selectedManagerId) return managersForUi
    const start = (managerPage - 1) * managersPerPage
    return managersForUi.slice(start, start + managersPerPage)
  }, [selectedManagerId, managersForUi, managerPage, managersPerPage])

  const selectedManagerProjects = useMemo(() => {
    if (!selectedManagerId) return []
    const projects = projectsByManagerId.get(selectedManagerId) ?? []
    return sortProjectsByDeadline(projects, sort)
  }, [projectsByManagerId, selectedManagerId, sort])

  const totalSelectedManagerProjectPages = useMemo(() => {
    if (!selectedManagerId) return 1
    return Math.max(1, Math.ceil(selectedManagerProjects.length / selectedManagerProjectsPerPage))
  }, [selectedManagerId, selectedManagerProjects.length, selectedManagerProjectsPerPage])

  const pagedSelectedManagerProjects = useMemo(() => {
    if (!selectedManagerId) return []
    const start = (selectedManagerProjectPage - 1) * selectedManagerProjectsPerPage
    return selectedManagerProjects.slice(start, start + selectedManagerProjectsPerPage)
  }, [
    selectedManagerId,
    selectedManagerProjects,
    selectedManagerProjectPage,
    selectedManagerProjectsPerPage,
  ])

  useEffect(() => {
    setManagerPage(1)
  }, [managersForUi.length, managersPerPage, qNorm, status, manager])

  useEffect(() => {
    setSelectedManagerProjectPage(1)
  }, [selectedManagerId, selectedManagerProjects.length, selectedManagerProjectsPerPage, qNorm, status, sort])

  const [success, setSuccess] = useState({ open: false, name: '', message: '' })
  const [completeSuccess, setCompleteSuccess] = useState({ open: false, name: '' })
  const [markCompleteModal, setMarkCompleteModal] = useState({ open: false, project: null })

  const handleProjectCreated = async (createdProject) => {
    setSuccess({
      open: true,
      name: createdProject?.name || '',
      message:
        projectModalMode === 'duplicate'
          ? 'duplicated successfully. Your team can now start working on the new project.'
          : 'created successfully. Your team has been notified and can start creating their tasks.',
    })
    window.clearTimeout(window.__projectCreatedTimer)
    window.__projectCreatedTimer = window.setTimeout(() => {
      setSuccess((s) => ({ ...s, open: false }))
    }, 4500)
    await queryClient.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
  }

  const handleCloseCreateProjectModal = () => {
    setOpenCreateProjectModal(false)
    setProjectModalMode('create')
    setDuplicateInitialValues(null)
  }

  const getManagerProjectsLabel = (staff) => {
    const name = staff?.name || 'this manager'
    if (name === 'You') {
      return {
        summary: 'your projects',
        detail: 'you manage',
      }
    }

    return {
      summary: `${name}'s projects`,
      detail: `${name} manages`,
    }
  }

  const handleViewAllManagerProjects = (managerId) => {
    if (!managerId) return
    setManager(String(managerId))
    setSelectedManagerProjectPage(1)
  }

  const openMarkCompleteModal = (proj) => {
    if (!proj?.id) return
    if (!isManagerOfProject(proj, myStaffId)) return
    setMarkCompleteModal({ open: true, project: proj })
  }

  const closeMarkCompleteModal = () => setMarkCompleteModal({ open: false, project: null })

  const handleConfirmMarkComplete = async () => {
    const proj = markCompleteModal.project
    if (!proj?.id) return

    try {
      await completeProject(proj.id)
      closeMarkCompleteModal()

      setCompleteSuccess({ open: true, name: proj?.name || '' })
      window.clearTimeout(window.__projectCompletedTimer)
      window.__projectCompletedTimer = window.setTimeout(() => {
        setCompleteSuccess((s) => ({ ...s, open: false }))
      }, 4500)

      await queryClient.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
      await queryClient.invalidateQueries([PROJECT_TRACKER_TASKS_QK])
    } catch (e) {
      console.error(e)
    }
  }

  const paginationCount = selectedManagerId
    ? selectedManagerProjects.length
    : managersForDisplay.length
  const paginationPage = selectedManagerId
    ? Math.max(0, selectedManagerProjectPage - 1)
    : Math.max(0, managerPage - 1)
  const paginationRowsPerPage = selectedManagerId
    ? selectedManagerProjectsPerPage
    : managersPerPage

  const location = useLocation()
  const stickyHeaderRef = React.useRef(null)
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0)

  useEffect(() => {
    const flash = location.state?.flash
    if (flash?.type === 'projectCreated') {
      setSuccess({
        open: true,
        name: flash?.name || '',
        message: 'created successfully. Your team has been notified and can start creating their tasks.',
      })

      window.clearTimeout(window.__projectCreatedTimer)
      window.__projectCreatedTimer = window.setTimeout(() => {
        setSuccess((s) => ({ ...s, open: false }))
      }, 4500)

      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [location.state])

  useEffect(() => {
    const node = stickyHeaderRef.current
    if (!node) return

    const updateStickyHeaderHeight = () => {
      setStickyHeaderHeight(node.getBoundingClientRect().height)
    }

    updateStickyHeaderHeight()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateStickyHeaderHeight) : null

    resizeObserver?.observe(node)
    window.addEventListener('resize', updateStickyHeaderHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateStickyHeaderHeight)
    }
  }, [])

  const showPager =
    activeTab === 0 &&
    (selectedManagerId ? totalSelectedManagerProjectPages > 1 : totalManagerPages > 1)

  return (
    <DashboardContainer headerTitle="Project Tracker" noPadding={true} childrenMaxWidth={true}>
      {/* scroll container */}
      <Box
        sx={{
          height: 'calc(100vh - 91px)',
          overflowY: 'auto',
          px: { xs: 2, lg: 3 },
          pt: 0,
        }}
      >
        <Box
          ref={stickyHeaderRef}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            bgcolor: 'gray-background.main',
            borderBottom: 1,
            borderColor: 'divider',
            pt: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pr: 2,
              gap: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleMainTabChange}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: '#2563eb',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2563eb',
                },
              }}
            >
              <Tab label="Projects" />
              {ENABLE_GANTT_CHART && <Tab label="Gantt Chart" />}
              <Tab label="Calendar" />
            </Tabs>

            <AdxHeaderButton icon={<AddIcon />} onClick={handleCreateProject} color="primary">
              <Typography
                sx={{
                  color: 'dark-text.main',
                  fontVariant: 'subtitle1',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Create project
              </Typography>
            </AdxHeaderButton>
          </Box>
        </Box>

        <CreateProjectModal
          open={openCreateProjectModal}
          onClose={handleCloseCreateProjectModal}
          onProjectCreated={handleProjectCreated}
          mode={projectModalMode}
          initialValues={duplicateInitialValues}
        />

        <EditProjectModal
          open={openEditProjectModal}
          projectId={editingProjectId}
          onClose={handleCloseEdit}
          onSaved={async () => {
            await queryClient.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
            await queryClient.invalidateQueries([PROJECT_TRACKER_TASKS_QK])
          }}
        />

        <MarkProjectCompleteModal
          open={markCompleteModal.open}
          projectName={markCompleteModal.project?.name}
          loading={completingProject}
          onClose={closeMarkCompleteModal}
          onConfirm={handleConfirmMarkComplete}
        />

        <Box width="100%">
          {activeTab === 0 && (
            <Box>
              <Collapse in={success.open} sx={{ mt: 0, mb: 3 }}>
                <Alert
                  icon={<CheckCircleIcon />}
                  severity="success"
                  onClose={() => setSuccess((s) => ({ ...s, open: false }))}
                  sx={{
                    bgcolor: '#E9FBEA',
                    color: 'rgba(0,0,0,0.75)',
                    borderLeft: '6px solid #22C55E',
                    borderRadius: 2,
                    alignItems: 'center',
                  }}
                >
                  Project <b>{success.name || '[Name]'}</b> {success.message}
                </Alert>
              </Collapse>

              <Collapse in={completeSuccess.open} sx={{ mt: 0, mb: 1 }}>
                <Alert
                  icon={<CheckCircleIcon />}
                  severity="success"
                  onClose={() => setCompleteSuccess((s) => ({ ...s, open: false }))}
                  sx={{
                    bgcolor: '#E9FBEA',
                    color: 'rgba(0,0,0,0.75)',
                    borderLeft: '6px solid #22C55E',
                    borderRadius: 2,
                    alignItems: 'center',
                  }}
                >
                  Project <b>{completeSuccess.name || '[Name]'}</b> marked as complete successfully.
                </Alert>
              </Collapse>

              {!success.open && !completeSuccess.open ? <Box sx={{ mt: 0 }} /> : null}

              <Box sx={{ mt: 1 }}>
                <Typography variant="h3" fontWeight={500} mb={0}>
                  Projects
                </Typography>
                <Typography variant="caption" fontWeight={400} mb={2}>
                  Project overview at a glance
                </Typography>
              </Box>

              <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                <TextField
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search a project"
                  size="small"
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      ...pillSx,
                      // height: 44,
                      px: 0,
                      backgroundColor: '#F7F9FA',
                    },
                    '& .MuiInputBase-root': {
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr 24px',
                      alignItems: 'center',
                      columnGap: '10px',
                      padding: '0 16px',
                      boxSizing: 'border-box',
                    },
                    '& input': {
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: 'normal',
                      color: '#000000',
                      textAlign: 'left',
                      '&::placeholder': {
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: 'normal',
                        color: '#8A8A8A',
                        opacity: 1,
                        textAlign: 'center',
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{
                          m: 0,
                          width: 16,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '& svg': { display: 'block' },
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.9998 14.0008L11.1064 11.1074" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#8A8A8A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </InputAdornment>
                    ),
                    endAdornment: <InputAdornment position="end" sx={{ width: 16 }} />,
                  }}
                />

                {/* ✅ YOUR EXISTING FILTER STACKS (inserted 그대로) */}
                <Stack direction="row" alignItems="center">
                  <IconButton aria-label="filter-status" sx={{ borderRadius: 2 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11.1856 23.25H9V11.8982L1.125 2.52319V0.75H22.5V2.51306L15 11.8881V19.4356L11.1856 23.25ZM10.5 21.75H10.5644L13.5 18.8144V11.3619L20.7895 2.25H2.85469L10.5 11.3518V21.75Z"
                        fill="#8A8A8A"
                      />
                    </svg>
                  </IconButton>

                  <PillSelect
                    variant="subtitle1"
                    value={status}
                    onChange={(v) => {
                      setStatusTouched(true)
                      setStatus(v)
                    }}
                    placeholder="Ongoing"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <g clipPath="url(#clip0_1557_4658)">
                          <path d="M0 8C0 3.582 3.582 0 8 0C12.418 0 16 3.582 16 8C16 12.418 12.418 16 8 16C3.584 15.9947 0.00533333 12.416 0 8.00067V8ZM1.6 8C1.6 11.5347 4.46533 14.4 8 14.4C11.5347 14.4 14.4 11.5347 14.4 8C14.4 4.46533 11.5347 1.6 8 1.6C4.46733 1.604 1.604 4.46733 1.6 7.99933V8ZM4.26667 8C4.26667 7.00986 4.66 6.06027 5.36014 5.36014C6.06027 4.66 7.00986 4.26667 8 4.26667C8.99014 4.26667 9.93973 4.66 10.6399 5.36014C11.34 6.06027 11.7333 7.00986 11.7333 8C11.7333 8.99014 11.34 9.93973 10.6399 10.6399C9.93973 11.34 8.99014 11.7333 8 11.7333C7.00986 11.7333 6.06027 11.34 5.36014 10.6399C4.66 9.93973 4.26667 8.99014 4.26667 8Z" fill="#8A8A8A" />
                        </g>
                        <defs>
                          <clipPath id="clip0_1557_4658">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    }
                    items={statusItems}
                    minWidth={170}
                    allowEmpty={false}
                    forceGrey={!statusTouched}
                  />
                </Stack>

                <Stack direction="row" alignItems="center">
                  <IconButton aria-label="filter-manager" sx={{ borderRadius: 2 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="22" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M11.1856 23.25H9V11.8982L1.125 2.52319V0.75H22.5V2.51306L15 11.8881V19.4356L11.1856 23.25ZM10.5 21.75H10.5644L13.5 18.8144V11.3619L20.7895 2.25H2.85469L10.5 11.3518V21.75Z"
                        fill="#8A8A8A"
                      />
                    </svg>
                  </IconButton>

                  <PillSelect
                    value={manager}
                    onChange={setManager}
                    placeholder="All managers"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12.667 13.8327C12.8438 13.8327 13.0134 13.7624 13.1384 13.6374C13.2634 13.5124 13.3337 13.3428 13.3337 13.166V12.3353C13.3363 10.4647 10.6843 8.99935 8.00033 8.99935C5.31633 8.99935 2.66699 10.4647 2.66699 12.3353V13.166C2.66699 13.3428 2.73723 13.5124 2.86225 13.6374C2.98728 13.7624 3.15685 13.8327 3.33366 13.8327H12.667ZM10.403 4.56868C10.403 4.88421 10.3408 5.19664 10.2201 5.48814C10.0994 5.77965 9.92238 6.04452 9.69927 6.26762C9.47616 6.49073 9.21129 6.66771 8.91979 6.78846C8.62828 6.9092 8.31585 6.97135 8.00033 6.97135C7.6848 6.97135 7.37237 6.9092 7.08086 6.78846C6.78936 6.66771 6.52449 6.49073 6.30138 6.26762C6.07827 6.04452 5.9013 5.77965 5.78055 5.48814C5.65981 5.19664 5.59766 4.88421 5.59766 4.56868C5.59766 3.93146 5.8508 3.32033 6.30138 2.86974C6.75197 2.41915 7.3631 2.16602 8.00033 2.16602C8.63755 2.16602 9.24868 2.41915 9.69927 2.86974C10.1499 3.32033 10.403 3.93146 10.403 4.56868Z"
                          stroke="#8A8A8A"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    }
                    items={managerItems}
                    minWidth={180}
                    allowEmpty={true}
                  />
                </Stack>

                <Stack direction="row" alignItems="center">
                  <IconButton aria-label="sort" sx={{ borderRadius: 2 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13.4702 7.52934C13.6108 7.66979 13.8014 7.74868 14.0002 7.74868C14.1989 7.74868 14.3895 7.66979 14.5302 7.52934L15.2502 6.80934V16.9993C15.2502 17.1983 15.3292 17.389 15.4698 17.5297C15.6105 17.6703 15.8012 17.7493 16.0002 17.7493C16.1991 17.7493 16.3898 17.6703 16.5305 17.5297C16.6711 17.389 16.7502 17.1983 16.7502 16.9993V6.80934L17.4702 7.52934C17.5388 7.60303 17.6216 7.66213 17.7136 7.70312C17.8056 7.74411 17.9049 7.76615 18.0056 7.76793C18.1063 7.76971 18.2064 7.75118 18.2998 7.71346C18.3931 7.67574 18.478 7.6196 18.5492 7.54838C18.6204 7.47716 18.6766 7.39233 18.7143 7.29894C18.752 7.20555 18.7705 7.10552 18.7687 7.00482C18.767 6.90411 18.7449 6.8048 18.7039 6.7128C18.6629 6.6208 18.6038 6.538 18.5302 6.46934L16.5302 4.46934C16.3895 4.32889 16.1989 4.25 16.0002 4.25C15.8014 4.25 15.6108 4.32889 15.4702 4.46934L13.4702 6.46934C13.3297 6.60997 13.2508 6.80059 13.2508 6.99934C13.2508 7.19809 13.3297 7.38871 13.4702 7.52934ZM8.75015 17.1893L9.47015 16.4693C9.53882 16.3957 9.62162 16.3365 9.71362 16.2956C9.80561 16.2546 9.90493 16.2325 10.0056 16.2307C10.1063 16.229 10.2064 16.2475 10.2998 16.2852C10.3931 16.3229 10.478 16.3791 10.5492 16.4503C10.6204 16.5215 10.6766 16.6064 10.7143 16.6997C10.752 16.7931 10.7705 16.8932 10.7687 16.9939C10.767 17.0946 10.7449 17.1939 10.7039 17.2859C10.6629 17.3779 10.6038 17.4607 10.5302 17.5293L8.53015 19.5293C8.38953 19.6698 8.1989 19.7487 8.00015 19.7487C7.8014 19.7487 7.61078 19.6698 7.47015 19.5293L5.47015 17.5293C5.39647 17.4607 5.33736 17.3779 5.29637 17.2859C5.25538 17.1939 5.23334 17.0946 5.23156 16.9939C5.22979 16.8932 5.24831 16.7931 5.28603 16.6997C5.32375 16.6064 5.3799 16.5215 5.45112 16.4503C5.52233 16.3791 5.60717 16.3229 5.70056 16.2852C5.79394 16.2475 5.89397 16.229 5.99468 16.2307C6.09538 16.2325 6.19469 16.2546 6.28669 16.2956C6.37869 16.3365 6.46149 16.3957 6.53015 16.4693L7.25015 17.1893V6.99934C7.25015 6.80043 7.32917 6.60966 7.46982 6.46901C7.61048 6.32836 7.80124 6.24934 8.00015 6.24934C8.19907 6.24934 8.38983 6.32836 8.53048 6.46901C8.67114 6.60966 8.75015 6.80043 8.75015 6.99934V17.1893Z"
                        fill="#8A8A8A"
                      />
                    </svg>
                  </IconButton>

                  <PillSelect
                    value={sort}
                    onChange={(v) => {
                      setSortTouched(true)
                      setSort(v)
                    }}
                    placeholder="Nearest Due"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M14.25 14.25H3.75V6H14.25M12 0.75V2.25H6V0.75H4.5V2.25H3.75C2.9175 2.25 2.25 2.9175 2.25 3.75V14.25C2.25 14.6478 2.40804 15.0294 2.68934 15.3107C2.97064 15.592 3.35218 15.75 3.75 15.75H14.25C14.6478 15.75 15.0294 15.592 15.3107 15.3107C15.592 15.0294 15.75 14.6478 15.75 14.25V3.75C15.75 3.35218 15.592 2.97064 15.3107 2.68934C15.0294 2.40804 14.6478 2.25 14.25 2.25H13.5V0.75M12.75 9H9V12.75H12.75V9Z" fill="#8A8A8A" />
                      </svg>
                    }
                    items={sortItems}
                    minWidth={170}
                    allowEmpty={false}
                    forceGrey={!sortTouched}
                  />
                </Stack>
              </Stack>

              <Box sx={{ mt: 3 }}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: stickyHeaderHeight,
                    zIndex: 10,
                    bgcolor: 'gray-background.main',
                    pb: 2,
                    pt: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 500, fontSize: 14, color: '#4a4a4a' }}>
                    Managed by:
                  </Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                  {myAccLoading || projectsLoading || tasksLoading ? (
                    <ProjectTrackerLoadingState
                      projectCardsPerRow={projectCardsPerRow}
                      selectedManagerId={selectedManagerId}
                    />
                  ) : managersForDisplay.length === 0 ? (
                    <Typography variant="caption">No managers</Typography>
                  ) : (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <Grid container spacing={3} sx={{ pb: showPager ? 20 : 6 }}>
                        {pagedManagers.map((s) => {
                          const isMe = s?.id === myStaffId
                          const managerStaff = isMe ? { ...s, name: 'You' } : s
                          const managerProjectsLabel = getManagerProjectsLabel(managerStaff)
                          const relatedProjectsRaw = projectsByManagerId.get(s.id) ?? []
                          const allManagerProjects = sortProjectsByDeadline(relatedProjectsRaw, sort)
                          const relatedProjects = selectedManagerId
                            ? pagedSelectedManagerProjects
                            : allManagerProjects.slice(0, managerOverviewProjectsLimit)
                          const hiddenProjectsCount = Math.max(
                            0,
                            allManagerProjects.length - relatedProjects.length
                          )

                          return (
                            <Grid item xs={12} md={selectedManagerId ? 12 : 4} key={s.id}>
                              <Box sx={{ mb: 2 }}>
                                <ProjectManager staff={managerStaff} />
                              </Box>

                              {relatedProjects.length === 0 ? (
                                <EmptyProjectStateCard />
                              ) : selectedManagerId ? (
                                <Grid container spacing={3}>
                                  {relatedProjects.map((proj) => {
                                    const canEdit = canEditProject(proj, myStaffId)
                                    const canDuplicate = isProjectMasterOfProject(proj, myStaffId)

                                    return (
                                      <Grid item xs={12} md={12 / projectCardsPerRow} key={proj.id}>
                                        <TaskProjectCard
                                          project={proj}
                                          defaultExpanded={false}
                                          canEdit={canEdit}
                                          canDuplicate={canDuplicate}
                                          onEdit={() => handleOpenEdit(proj)}
                                          onDuplicate={() => handleDuplicateProject(proj)}
                                          onMarkComplete={openMarkCompleteModal}
                                          sortTasks
                                        />
                                      </Grid>
                                    )
                                  })}
                                </Grid>
                              ) : (
                                <Stack spacing={3}>
                                  {relatedProjects.map((proj) => {
                                    const canEdit = canEditProject(proj, myStaffId)
                                    const canDuplicate = isProjectMasterOfProject(proj, myStaffId)

                                    return (
                                      <TaskProjectCard
                                        key={proj.id}
                                        project={proj}
                                        defaultExpanded={false}
                                        canEdit={canEdit}
                                        canDuplicate={canDuplicate}
                                        onEdit={() => handleOpenEdit(proj)}
                                        onDuplicate={() => handleDuplicateProject(proj)}
                                        onMarkComplete={openMarkCompleteModal}
                                        sortTasks
                                      />
                                    )
                                  })}

                                  {hiddenProjectsCount > 0 ? (
                                    <Box
                                      sx={{
                                        border: '1px solid rgba(37, 99, 235, 0.16)',
                                        borderRadius: 3,
                                        px: 2,
                                        py: 1.75,
                                        mb: 3,
                                        backgroundColor: '#F8FBFF',
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ color: '#2563EB', fontWeight: 700, lineHeight: 1.3 }}
                                      >
                                        +{hiddenProjectsCount} more project{hiddenProjectsCount > 1 ? 's' : ''}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#4B5563', display: 'block', mt: 0.5, mb: 1.25 }}
                                      >
                                        See all {allManagerProjects.length} projects {managerProjectsLabel.detail}.
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleViewAllManagerProjects(s.id)}
                                        endIcon={<KeyboardArrowRightIcon fontSize="small" />}
                                        sx={{
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          borderRadius: 999,
                                          boxShadow: 'none',
                                          px: 1.5,
                                          backgroundColor: '#2563EB',
                                          color: '#FFFFFF',
                                          '&:hover': {
                                            backgroundColor: '#1D4ED8',
                                            boxShadow: 'none',
                                          },
                                        }}
                                      >
                                        View all {managerProjectsLabel.summary}
                                      </Button>
                                    </Box>
                                  ) : null}
                                </Stack>
                              )}
                            </Grid>
                          )
                        })}
                      </Grid>

                      {showPager && (
                        <Box
                          sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: `${sidebarWidth}px`,
                            right: 0,
                            zIndex: 10,
                            borderTop: '1px solid #e0e0e0',
                            bgcolor: '#FFFFFF',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderRadius: 0,
                            boxShadow: 'none',
                            overflow: 'hidden',
                          }}
                        >
                          <TablePagination
                            component="div"
                            count={paginationCount}
                            page={paginationPage}
                            onPageChange={(event, newPage) => {
                              if (selectedManagerId) {
                                setSelectedManagerProjectPage(newPage + 1)
                                return
                              }
                              setManagerPage(newPage + 1)
                            }}
                            rowsPerPage={paginationRowsPerPage}
                            rowsPerPageOptions={[]}
                            showFirstButton={true}
                            showLastButton={true}
                            backIconButtonProps={{ size: 'small' }}
                            nextIconButtonProps={{ size: 'small' }}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                            sx={{
                              '& .MuiTablePagination-toolbar': {
                                minHeight: '48px',
                                paddingLeft: 0,
                                paddingRight: 0,
                                alignItems: 'center',
                              },
                              '& .MuiTablePagination-displayedRows': {
                                marginBottom: 0,
                                marginTop: 0,
                                display: 'flex',
                                alignItems: 'center',
                                height: '100%',
                              },
                              '& .MuiTablePagination-actions': {
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {ENABLE_GANTT_CHART && activeTab === 1 && (
            <TrackerGanttChart
              projects={ganttProjects}
              tasks={ganttTasks}
              currentStaffId={myStaffId}
              loading={projectsLoading || tasksLoading || (!canViewAllGanttProjects && myProjectsLoading)}
              onTaskUpdated={async () => {
                await queryClient.invalidateQueries([PROJECT_TRACKER_PROJECTS_QK])
                await queryClient.invalidateQueries(['my-projects'])
                await queryClient.invalidateQueries([PROJECT_TRACKER_TASKS_QK])
              }}
            />
          )}

          {activeTab === calendarTabIndex && (
            <div>
              <TrackerCalendar />
            </div>
          )}
        </Box>
      </Box>
    </DashboardContainer>
  )
}

export default ProjectTracker
